"use client"

import { useCallback, useEffect } from "react"
import { flushSync } from "react-dom"
import { waitForAnimationFrame } from "../../lib/helpers"
import { useModalContext } from "./useModalContext"

export const useModalClose = () => {
  const { setOpen } = useModalContext()
  return useCallback(() => setOpen(false), [setOpen])
}

export const useModalShake = () => {
  const { setShake } = useModalContext()
  return useCallback(() => {
    // Sequence: (1) ensure shake is cleared, then (2) set on the next tick
    flushSync(() => {
      setShake(false)
    })
    waitForAnimationFrame(() => setShake(true))
  }, [setShake])
}

export const useOnModalRequestClose = (onRequestClose?: () => "deny" | "allow") => {
  const context = useModalContext()
  useEffect(() => {
    if (!onRequestClose) {
      return
    }
    context.checkAllowClose.current.add(onRequestClose)
    return () => {
      context.checkAllowClose.current.delete(onRequestClose)
    }
  }, [onRequestClose, context.checkAllowClose])
}

export const useModalController = ({
  onRequestClose,
}: {
  /**
   * Use this to block the modal from closing due to clicking outside or pressing
   * escape. Typically this should be used to make the modal harder to accidentally
   * close when this would destroy the users work, or when a request is pending and
   * we want to keep the modal locked open until the request is finished.
   *
   * @example
   * const {shake} = useModalController({
   *     onRequestClose: () => {
   *         if (someForm.isDirty) {
   *             shake();
   *             return false;
   *         }
   *         if (someForm.isSubmitting) {
   *             return false;
   *         }
   *         return true;
   *     }
   * })
   */
  onRequestClose?: () => "deny" | "allow"
} = {}): {
  /** Plays a short shake animation on the modal. */
  shake: () => void
  /** Imperatively closes the modal. */
  close: () => void
} => {
  const shake = useModalShake()
  const close = useModalClose()
  useOnModalRequestClose(onRequestClose)
  return { close, shake }
}
